import { ref, watch } from 'vue';

import { defineStore } from 'pinia';

import Api from '@/api/Api';

import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import { useMapFeaturesStore } from '@/stores/mapFeaturesStore';
import { useSubNetworksStore } from '@/stores/subNetworksStore';

export const storeVisibilityItemInObject = (visibilityObject, item, showSatellitesInSolution) => {
	visibilityObject[item.gnss] = {
		valueBright: item.satellites,
		valuePale: showSatellitesInSolution ? item.satellites_in_solution : null,
		currentDatetimeBright: item.current_datetime,
		currentDatetimePale: showSatellitesInSolution ? item.current_datetime_in_solution : null,
		// valueIsOld относится к pale значению для featureType === FEATURE_TYPE.baseStation
		// valueIsOld относится к bright значению для featureType === FEATURE_TYPE.gridpoint
		valueIsOld: item.value_is_old,
		signals: item.signals,
		rtcmMessages: item.rtcm_messages,
		minValue: null,
		maxValue: null,
	};
};


const storeBase = featureType => defineStore('gnssVisibilityStore' + featureType, () => {
	const dbItems = ref(new Map());
	// Каждый элемент соответствует контракту GnssTooltipDisplayItem
	// (см. @/stores/types/mapStoreContracts.ts), который читает useMapData.
	// TODO(TS_MIGRATION): типизировать через GnssTooltipDisplayItem[] после перевода стора на TS.
	const displayItems = ref([]);
	const nonNullGnss = ref([]);
	const isVisible = ref(false);

	// эта переменная нужна для того, чтобы не загружать данные о видимости спутников для gridpoints, если они не нужны
	// по ней считается showGridpointsOnly через отрицание
	const needsVisibilityData = ref(true);

	const featuresStore = useMapFeaturesStore();

	const getDisplayItems = () => {
		if (!isVisible.value) {
			return [];
		}
		const displayItemsResult = featuresStore.features.filter(item => item.featureType === featureType).map(feature => {
			const mountpoint = feature.featureData.mntpnt_int;
			return {
				...feature,
				stats: dbItems.value.has(mountpoint) ? dbItems.value.get(mountpoint) : null,
			};
		});
		return displayItemsResult;
	};

	const updateDisplayItems = () => {
		displayItems.value = getDisplayItems();
	}

	watch(() => featuresStore.features, updateDisplayItems);

	const updateDbItems = dbItemsValue => {
		// exclude GNSS with 0 satellites for all BS
		let satelliteCounts = new Map();
		for (let item of dbItemsValue) {
			if (!satelliteCounts.has(item.gnss)) {
				satelliteCounts.set(item.gnss, 0);
			}
			satelliteCounts.set(item.gnss, satelliteCounts.get(item.gnss) + item.satellites);
		}
		let showGnss = [];
		for (const [gnss, count] of satelliteCounts) {
			if (count > 0) {
				showGnss.push(gnss);
			}
		}
		let sortedItems = new Map();
		for (let item of dbItemsValue) {
			if (!showGnss.includes(item.gnss)) continue;
			if (!sortedItems.has(item.mountpoint)) {
				sortedItems.set(item.mountpoint, {});
			}
			storeVisibilityItemInObject(sortedItems.get(item.mountpoint), item, featureType === FEATURE_TYPE.baseStation);
		}
		dbItems.value = sortedItems;
		nonNullGnss.value = [...new Set(showGnss)];
		updateDisplayItems();
	}

	const handleGnssVisibilityGet = responseData => {
		updateDbItems(responseData.items);
	}
	const handleGnssVisibilityGetError = error => {
		if (error.response.status !== 401) {
			Api.logError(error);
		}
	}
	const subNetworksStore = useSubNetworksStore();
	const refreshBaseStationMode = selectChanged => {
		if (isVisible.value) {
			// если периодическое обновление, то не очищаем данные, чтобы не моргало
			// если выбор другой сети, то очищаем данные
			if (selectChanged) {
				updateDbItems([]);
			}
			Api.gnssVisibilityBaseStationsGet(subNetworksStore.subNetworkSelectedId, handleGnssVisibilityGet, handleGnssVisibilityGetError);
		}
	}
	const refreshGridpointMode = selectChanged => {
		if (isVisible.value && needsVisibilityData.value) {
			// если периодическое обновление, то не очищаем данные, чтобы не моргало
			// если выбор другого c-net, то очищаем данные
			if (selectChanged) {
				updateDbItems([]);
			}
			if (subNetworksStore.cnetSelectedId == null) {
				updateDbItems([]);
			} else {
				Api.gnssVisibilityGridpointsGet(subNetworksStore.cnetSelectedId, handleGnssVisibilityGet, handleGnssVisibilityGetError);
			}
		}
	}
	const refresh = () => {
		if (featureType === FEATURE_TYPE.baseStation) {
			refreshBaseStationMode(false);
		} else {
			refreshGridpointMode(false);
		}
	}
	if (featureType === FEATURE_TYPE.baseStation) {
		watch(() => subNetworksStore.subNetworkSelectedId, () => { refreshBaseStationMode(true) });
	} else {
		watch(() => subNetworksStore.cnetSelectedId, () => { refreshGridpointMode(true) });
	}

	const setVisibility = visibility => {
		isVisible.value = visibility;
		updateDisplayItems();
		refresh();
	}

	return {
		dbItems,
		displayItems,
		nonNullGnss,
		isVisible,
		needsVisibilityData,
		refresh,
		setVisibility,
	}
});

export const useGnssVisibilityStore = storeBase('baseStation');
export const useGnssPerGridpointStore = storeBase('gridpoint');
